import React from "react";
import ReactDOM from "react-dom";
import reportWebVitals from "./reportWebVitals";

import { CssBaseline, ThemeProvider } from "@material-ui/core";

import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  ApolloProvider,
  ApolloLink,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";

import App from "./App";

import "./index.css";
import {
  LayoutProvider,
  UserStorageKeys,
  UserProvider,
  CultureProvider,
  EnvironmentProvider,
} from "./context";
import { DefaultTheme } from "./ui/themes";
import { SnackbarProvider } from "notistack";

import variables from "./environment/environment.variables";

const apolloLink = createHttpLink({
  uri: variables.graphqlBaseUrl || "http://localhost:4000/graphql",
});

const apolloCleanTypenameLink = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    const omitTypename = (key: string, value: any) =>
      key === "__typename" ? undefined : value;
    operation.variables = JSON.parse(
      JSON.stringify(operation.variables),
      omitTypename
    );
  }
  return forward(operation).map((data) => {
    return data;
  });
});

const apolloAuthLink = setContext((_, { headers }) => {
  const token = sessionStorage.getItem(UserStorageKeys.USER_TOKEN);

  const buildingString = sessionStorage.getItem(UserStorageKeys.USER_BUILDING);
  let buildingId = "";
  if (buildingString) {
    buildingId = JSON.parse(buildingString).id;
  }

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
      "x-building-id": buildingId,
    },
  };
});

const apolloClient = new ApolloClient({
  link: ApolloLink.from([apolloCleanTypenameLink, apolloAuthLink, apolloLink]),
  cache: new InMemoryCache({
    dataIdFromObject: (obj) => {
      // Used to prevent spreading the same result upon a missing `id` key
      // (or null key).
      const key = obj.id ? `${obj.__typename}-${JSON.stringify(obj)}` : void 0;
      return key;
    },
  }),
  defaultOptions: {
    mutate: {
      fetchPolicy: "no-cache",
    },
    query: {
      fetchPolicy: "network-only",
    },
  },
});

ReactDOM.render(
  <EnvironmentProvider>
    <CultureProvider>
      <ApolloProvider client={apolloClient}>
        <LayoutProvider>
          <UserProvider apolloClient={apolloClient}>
            <ThemeProvider theme={DefaultTheme}>
              <CssBaseline />
              <SnackbarProvider>
                <App />
              </SnackbarProvider>
            </ThemeProvider>
          </UserProvider>
        </LayoutProvider>
      </ApolloProvider>
    </CultureProvider>
  </EnvironmentProvider>,
  document.getElementById("root")
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
